/* eslint-disable */
import { useState, useEffect } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useTheme } from '@mui/material';
import axios from 'axios';
import { getTourStepsByRole } from '../../config/tourSteps';
import { getUserDetails } from '../../utils';
import { useMutationCompleteTour } from '../../hooks/react-query/useTour';
import { useNavigate } from 'react-router-dom';

const OnboardingTour = ({ run, onComplete }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const userDetails = getUserDetails();
  const [steps, setSteps] = useState([]);
  const [runTour, setRunTour] = useState(false);
  const [isCompleting, setIsCompleting] = useState(false);

  // React Query hook for completing tour
  const { mutate: completeTour } = useMutationCompleteTour(
    userDetails?.token,
    async (response) => {
      setIsCompleting(false);
      // Check if backend marked hasCompletedTour as true
      // Backend returns user object in response.data.data
      if (response?.data?.data?.hasCompletedTour === true) {
        // Call profile API to get updated user data
        try {
          const profileResponse = await axios.get(
            `${process.env.REACT_APP_SERVER_URL}/api/auth/profile`,
            {
              headers: {
                Authorization: `Bearer ${userDetails?.token}`,
              },
            }
          );
          
          if (profileResponse?.data?.data) {
            const updatedUserData = {
              ...profileResponse.data.data,
              token: userDetails?.token,
            };
            localStorage.setItem('user', JSON.stringify(updatedUserData));
          }
        } catch (error) {
          console.error('Error fetching profile after tour completion:', error);
          // Fallback: update localStorage directly
          const currentUser = getUserDetails();
          if (currentUser) {
            currentUser.hasCompletedTour = true;
            localStorage.setItem('user', JSON.stringify(currentUser));
          }
        }
      } else {
        // Fallback: update localStorage directly
        const currentUser = getUserDetails();
        if (currentUser) {
          currentUser.hasCompletedTour = true;
          localStorage.setItem('user', JSON.stringify(currentUser));
        }
      }

      if (onComplete) {
        onComplete();
      }
    },
    (error) => {
      setIsCompleting(false);
      console.error('Error completing tour:', error);
      // Fallback: update localStorage directly even on error
      const currentUser = getUserDetails();
      if (currentUser) {
        currentUser.hasCompletedTour = true;
        localStorage.setItem('user', JSON.stringify(currentUser));
      }
      if (onComplete) {
        onComplete();
      }
    }
  );

  useEffect(() => {
    if (userDetails?.role) {
      const tourSteps = getTourStepsByRole(userDetails.role);
      setSteps(tourSteps);
    }
  }, [userDetails?.role]);

  useEffect(() => {
    setRunTour(run);
  }, [run]);

  const handleJoyrideCallback = async (data) => {
    const { status, type, action, index, lifecycle, step } = data;

    // Handle step positioning and spotlight alignment
    if (type === EVENTS.STEP_BEFORE) {
      // Get the target element
      const target = step?.target;
      if (target) {
        const element = typeof target === 'string' ? document.querySelector(target) : target;
        if (element) {
          // Ensure element is in view and properly measured
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          // Wait for scroll to complete and element to settle
          await new Promise(resolve => setTimeout(resolve, 150));
        }
      }
    }

    if (type === EVENTS.TARGET_NOT_FOUND) {
      console.warn('Tour target not found:', step?.target);
    }

    // Force recalculation of spotlight position when step starts
    if (type === EVENTS.STEP_AFTER) {
      // Small delay to allow for smoother transitions
      await new Promise(resolve => setTimeout(resolve, 50));
      // Force recalculation
      window.dispatchEvent(new Event('resize'));
    }

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) && !isCompleting) {
      setRunTour(false);
      setIsCompleting(true);
      // Call the complete tour mutation using react-query
      completeTour();
    }
  };

  if (!steps || steps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={steps}
      run={runTour}
      continuous
      showProgress
      showSkipButton
      callback={handleJoyrideCallback}
      disableOverlayClose
      disableScrolling={false}
      scrollToFirstStep={true}
      scrollOffset={120}
      spotlightPadding={5}
      disableScrollParentFix={false}
      floaterProps={{
        disableAnimation: false,
        options: {
          offset: 12,
        },
        styles: {
          arrow: {
            length: 10,
            spread: 20,
          },
          floater: {
            filter: 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.3))',
          },
        },
      }}
      styles={{
        options: {
          primaryColor: theme.palette.primary.main,
          textColor: theme.palette.text.primary,
          backgroundColor: theme.palette.background.paper,
          overlayColor: 'rgba(0, 0, 0, 0.6)',
          spotlightShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
          zIndex: 10000,
          arrowColor: theme.palette.background.paper,
        },
        spotlight: {
          marginTop: 140,
          borderRadius: 8,
          transition: 'all 0.3s ease-in-out',
        },
        overlay: {
          mixBlendMode: 'hard-light',
        },
        tooltip: {
          borderRadius: 12,
          padding: 20,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        },
        tooltipContainer: {
          textAlign: 'left',
        },
        tooltipContent: {
          fontSize: '0.95rem',
          lineHeight: 1.6,
          padding: '10px 0',
        },
        buttonNext: {
          backgroundColor: theme.palette.primary.main,
          borderRadius: 8,
          fontSize: '0.9rem',
          padding: '8px 16px',
        },
        buttonBack: {
          color: theme.palette.text.secondary,
          marginRight: 10,
        },
        buttonSkip: {
          color: theme.palette.text.secondary,
        },
      }}
      locale={{
        back: 'Back',
        close: 'Close',
        last: 'Finish',
        next: 'Next',
        skip: 'Skip Tour',
      }}
    />
  );
};

export default OnboardingTour;
